import { domainsApi } from '@gl/api-client';
import type { AddDomainInput, DomainDto } from '@gl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

const domains = domainsApi(api);

export const domainsKey = (localId: string) => ['locals', localId, 'domains'] as const;

export function useDomains(localId: string, enabled = true) {
  return useQuery({
    queryKey: domainsKey(localId),
    queryFn: () => domains.list(localId),
    enabled,
  });
}

function useInvalidate(localId: string) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: domainsKey(localId) });
}

export function useAddDomain(localId: string) {
  const invalidate = useInvalidate(localId);
  return useMutation({
    mutationFn: (body: AddDomainInput) => domains.add(localId, body),
    onSuccess: () => void invalidate(),
  });
}

export function useVerifyDomain(localId: string) {
  const invalidate = useInvalidate(localId);
  return useMutation({
    mutationFn: (d: DomainDto) => domains.verify(localId, d.id),
    onSettled: () => void invalidate(),
  });
}

export function useRemoveDomain(localId: string) {
  const invalidate = useInvalidate(localId);
  return useMutation({
    mutationFn: (d: DomainDto) => domains.remove(localId, d.id),
    onSuccess: () => void invalidate(),
  });
}
